import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import {
  ShieldCheck, Upload, Trash2, Loader2, CheckCircle2, AlertTriangle,
  CalendarClock, Lock, Sparkles,
} from "lucide-react";
import VerifactuOnboarding from "./VerifactuOnboarding";

/**
 * VeriFactu settings panel — per-company.
 *
 * Lets the user:
 *  - Upload their PKCS#12 digital certificate (FNMT, Camerfirma, etc.).
 *  - Toggle real emission to AEAT (sandbox by default).
 *  - See certificate expiry + warnings (NIF mismatch, soon-to-expire).
 *
 * The private key + password are encrypted at rest with the master key.
 * Nothing sensitive is exposed via API after upload.
 */
export default function VerifactuSettings({ companyId }) {
  const { lang } = useI18n();
  const t = (es, ca) => (lang === "es" ? es : ca);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [uploading, setUploading] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/companies/${companyId}/verifactu`);
      setStatus(data);
      // Auto-open wizard if not yet fully configured AND user hasn't skipped
      // recently (within last 24h). Use localStorage so we don't nag.
      const skippedAt = parseInt(
        localStorage.getItem(`verifactu_onboarding_skipped_${companyId}`) || "0",
        10
      );
      const skippedRecently = skippedAt && (Date.now() - skippedAt < 24 * 3600 * 1000);
      const fullyConfigured = data?.certificate && data?.enabled;
      if (!fullyConfigured && !skippedRecently) {
        // Slight delay so users see the tab content first
        setTimeout(() => setShowWizard(true), 400);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (companyId) load(); }, [companyId]); // eslint-disable-line

  const onUpload = async () => {
    if (!fileRef.current?.files?.length) {
      toast.error(t("Selecciona un archivo .p12", "Selecciona un fitxer .p12"));
      return;
    }
    if (!password) {
      toast.error(t("Introduce la contraseña del certificado", "Introdueix la contrasenya del certificat"));
      return;
    }
    const fd = new FormData();
    fd.append("file", fileRef.current.files[0]);
    setUploading(true);
    try {
      const { data } = await api.post(
        `/companies/${companyId}/verifactu/cert?password=${encodeURIComponent(password)}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(t("Certificado guardado correctamente", "Certificat desat correctament"));
      if (data.warning) toast.warning(data.warning, { duration: 8000 });
      setPassword("");
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setUploading(false); }
  };

  const onDelete = async () => {
    if (!window.confirm(t(
      "¿Eliminar el certificado? VeriFactu se desactivará automáticamente.",
      "Eliminar el certificat? VeriFactu es desactivarà automàticament."
    ))) return;
    try {
      await api.delete(`/companies/${companyId}/verifactu/cert`);
      toast.success(t("Certificado eliminado", "Certificat eliminat"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const toggleEnabled = async (enabled) => {
    try {
      await api.put(`/companies/${companyId}/verifactu`, {
        enabled, environment: status?.environment || "test",
      });
      toast.success(enabled
        ? t("VeriFactu activado (entorno pruebas)", "VeriFactu activat (entorn proves)")
        : t("VeriFactu desactivado", "VeriFactu desactivat"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const switchEnv = async (environment) => {
    try {
      await api.put(`/companies/${companyId}/verifactu`, {
        enabled: status?.enabled || false, environment,
      });
      await load();
      toast.success(t(
        `Entorno: ${environment === "prod" ? "PRODUCCIÓN" : "Pruebas"}`,
        `Entorn: ${environment === "prod" ? "PRODUCCIÓ" : "Proves"}`
      ));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" /></div>;
  }

  const cert = status?.certificate;
  const meta = cert?.meta || {};
  const expiryColor =
    meta.expiry_status === "expired" ? "text-rose-300"
    : meta.expiry_status === "expiring_soon" ? "text-amber-300"
    : "text-emerald-300";

  return (
    <div className="space-y-4" data-testid="verifactu-settings">
      <VerifactuOnboarding
        open={showWizard}
        onClose={() => setShowWizard(false)}
        onComplete={load}
        companyId={companyId}
        status={status}
      />
      <div className="rounded-xl border border-blue-500/20 bg-blue-500/[0.04] p-4 flex items-start gap-3">
        <ShieldCheck className="text-blue-300 flex-shrink-0 mt-0.5" size={20} />
        <div className="flex-1 text-sm">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
            <div className="font-semibold text-blue-100">
              {t("Sobre VeriFactu", "Sobre VeriFactu")}
            </div>
            <button type="button"
                    onClick={() => setShowWizard(true)}
                    className="text-xs px-2 py-1 rounded-md bg-blue-500/20 hover:bg-blue-500/30 text-blue-200 border border-blue-400/30 flex items-center gap-1"
                    data-testid="verifactu-open-wizard">
              <Sparkles size={11} /> {t("Guía rápida", "Guia ràpida")}
            </button>
          </div>
          <p className="text-slate-300 leading-relaxed">
            {t(
              "Sistema oficial de la AEAT que registra cada factura emitida en tiempo real. Obligatorio desde 01/07/2025 para sociedades y 01/01/2026 para autónomos.",
              "Sistema oficial de l'AEAT que registra cada factura emesa en temps real. Obligatori des de 01/07/2025 per a societats i 01/01/2026 per a autònoms."
            )}
          </p>
          <p className="text-slate-400 mt-2 text-xs">
            {t(
              "Necesitas un certificado digital (FNMT, Camerfirma, etc.) en formato .p12 o .pfx. Tu clave privada se cifra con AES-128 y nunca sale del servidor.",
              "Necessites un certificat digital (FNMT, Camerfirma, etc.) en format .p12 o .pfx. La teva clau privada es xifra amb AES-128 i mai surt del servidor."
            )}
          </p>
        </div>
      </div>

      {!cert ? (
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5"
             data-testid="verifactu-upload-section">
          <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <Upload size={14} /> {t("Subir certificado", "Pujar certificat")}
          </h4>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">
                {t("Archivo .p12 / .pfx", "Fitxer .p12 / .pfx")}
              </label>
              <input
                ref={fileRef}
                type="file"
                accept=".p12,.pfx,application/x-pkcs12"
                className="block w-full text-sm text-slate-300 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-blue-500/20 file:text-blue-200 hover:file:bg-blue-500/30"
                data-testid="verifactu-file-input"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block flex items-center gap-1">
                <Lock size={11} /> {t("Contraseña del certificado", "Contrasenya del certificat")}
              </label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-white/5 border-white/10"
                data-testid="verifactu-password-input"
              />
            </div>
            <Button
              onClick={onUpload}
              disabled={uploading}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="verifactu-upload-btn"
            >
              {uploading ? <Loader2 className="mr-2 animate-spin" size={14} /> : <Upload size={14} className="mr-2" />}
              {t("Subir y validar", "Pujar i validar")}
            </Button>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-5"
             data-testid="verifactu-cert-card">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="text-emerald-400" size={18} />
              <h4 className="text-sm font-semibold text-white">
                {t("Certificado configurado", "Certificat configurat")}
              </h4>
            </div>
            <button
              type="button"
              onClick={onDelete}
              className="text-xs text-rose-300 hover:text-rose-200 flex items-center gap-1"
              data-testid="verifactu-delete-btn"
            >
              <Trash2 size={12} /> {t("Eliminar", "Eliminar")}
            </button>
          </div>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
            <dt className="text-slate-400">{t("Titular", "Titular")}</dt>
            <dd className="text-slate-100 font-medium" data-testid="cert-cn">
              {meta.subject?.common_name || "—"}
            </dd>
            <dt className="text-slate-400">{t("Emisor", "Emissor")}</dt>
            <dd className="text-slate-100">{meta.issuer?.common_name || "—"}</dd>
            <dt className="text-slate-400">NIF</dt>
            <dd className="text-slate-100 font-mono" data-testid="cert-nif">
              {meta.nif_from_cert || "—"}
            </dd>
            <dt className="text-slate-400 flex items-center gap-1">
              <CalendarClock size={11} /> {t("Caduca", "Caduca")}
            </dt>
            <dd className={`font-medium ${expiryColor}`} data-testid="cert-expiry">
              {meta.not_after?.slice(0, 10) || "—"}
              {meta.days_remaining !== undefined && (
                <span className="text-slate-500 ml-1">
                  ({meta.days_remaining} {t("días", "dies")})
                </span>
              )}
            </dd>
          </dl>
          {meta.expiry_status === "expiring_soon" && (
            <div className="mt-3 text-amber-200 text-xs flex items-center gap-1.5">
              <AlertTriangle size={12} />
              {t("El certificado caduca pronto. Renuévalo antes de la fecha.",
                 "El certificat caduca aviat. Renova'l abans de la data.")}
            </div>
          )}
          {meta.expiry_status === "expired" && (
            <div className="mt-3 text-rose-300 text-xs flex items-center gap-1.5"
                 data-testid="cert-expired-warning">
              <AlertTriangle size={12} />
              {t("Certificado CADUCADO. AEAT rechazará todas las facturas.",
                 "Certificat CADUCAT. AEAT rebutjarà totes les factures.")}
            </div>
          )}
        </div>
      )}

      {/* Toggle + environment selector */}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5"
           data-testid="verifactu-toggle-section">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-sm font-semibold text-white mb-1">
              {t("Envío real a AEAT", "Enviament real a AEAT")}
            </div>
            <div className="text-xs text-slate-400">
              {status?.enabled
                ? t("Cada factura emitida se envía automáticamente al AEAT",
                    "Cada factura emesa s'envia automàticament a AEAT")
                : t("Solo se genera el QR localmente; nada se envía a AEAT",
                    "Només es genera el QR localment; res s'envia a AEAT")}
            </div>
          </div>
          <button
            type="button"
            onClick={() => toggleEnabled(!status?.enabled)}
            disabled={!cert}
            className={`relative inline-flex h-7 w-13 items-center rounded-full transition ${
              status?.enabled ? "bg-emerald-500" : "bg-slate-600"
            } ${!cert ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
            data-testid="verifactu-toggle"
            style={{ width: 52 }}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
              status?.enabled ? "translate-x-7" : "translate-x-1"
            }`} />
          </button>
        </div>

        {cert && (
          <div className="mt-4 flex items-center gap-2 text-xs">
            <span className="text-slate-400">{t("Entorno:", "Entorn:")}</span>
            <button
              type="button"
              onClick={() => switchEnv("test")}
              className={`px-2.5 py-1 rounded-md border ${
                status?.environment === "test"
                  ? "bg-amber-500/20 border-amber-400/50 text-amber-100"
                  : "bg-white/[0.02] border-white/10 text-slate-400 hover:bg-white/[0.05]"
              }`}
              data-testid="env-test-btn"
            >
              {t("Pruebas (preproducción)", "Proves (preproducció)")}
            </button>
            <button
              type="button"
              onClick={() => switchEnv("prod")}
              className={`px-2.5 py-1 rounded-md border ${
                status?.environment === "prod"
                  ? "bg-rose-500/20 border-rose-400/50 text-rose-100"
                  : "bg-white/[0.02] border-white/10 text-slate-400 hover:bg-white/[0.05]"
              }`}
              data-testid="env-prod-btn"
            >
              {t("Producción (REAL)", "Producció (REAL)")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
